import { NavLink } from 'react-router-dom';

import { AllocabLogo } from './AllocabLogo';
import { FEATURES } from '../../constants';
import { useAccount } from '../../contexts/AccountContext';
import type { ExtranetPermission } from '../../domain/access';

interface NavItem {
  to: string;
  label: string;
  permission: ExtranetPermission;
  end?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Tableau de bord', permission: 'extranet-trips', end: true },
  { to: '/incidents', label: 'Incidents', permission: 'extranet-incidents' },
  { to: '/exports', label: 'Export', permission: 'extranet-exports' },
  { to: '/invoices', label: 'Facturation', permission: 'extranet-billing' },
  ...(FEATURES.lostFound
    ? [{ to: '/lost-found', label: 'Objets trouvés', permission: 'extranet-trips' as const }]
    : []),
];

export const Sidebar = () => {
  const { me, can, isAdmin, canInvite } = useAccount();

  // `/admin` ne se garde pas sur une permission : un membre sans
  // `extranet-admin` y entre pour inviter, et n'y voit que les invitations —
  // le libellé le dit plutôt que de promettre une administration complète.
  const visibleItems: Array<{ to: string; label: string; end?: boolean }> = [
    ...NAV_ITEMS.filter(item => can(item.permission)),
    ...(isAdmin || canInvite ? [{ to: '/admin', label: isAdmin ? 'Administration' : 'Invitations' }] : []),
  ];

  return (
    <aside className="w-[240px] shrink-0 border-r border-extranet-border bg-extranet-card flex flex-col">
      {/*
        Pas d'identifiant de compte sous le logo : faute de nom lisible côté
        API, on n'y affichait que la clé technique (`Passengeraccount_…`), qui
        ne dit rien au client (retrait demandé par Alexandre, 14 septembre 2026).
      */}
      <div className="px-6 py-5 border-b border-extranet-border">
        <div className="flex items-baseline gap-2">
          <AllocabLogo className="h-5 w-auto text-extranet-text-primary" />
          <span className="font-mono text-[11px] uppercase tracking-wide text-extranet-text-tertiary">Extranet</span>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 flex flex-col gap-0.5">
        {visibleItems.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              [
                'px-3 py-2 rounded-md text-sm transition-colors duration-100',
                isActive
                  ? 'bg-extranet-card-muted text-extranet-text-primary font-medium'
                  : 'text-extranet-text-secondary hover:bg-extranet-card-muted hover:text-extranet-text-primary',
              ].join(' ')
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="px-6 py-4 border-t border-extranet-border flex flex-col gap-1">
        <p className="text-xs text-extranet-text-secondary truncate" title={me.fullName}>
          {me.fullName}
        </p>
        <p className="text-extranet-text-tertiary text-xs">v0.1.0</p>
      </div>
    </aside>
  );
};
